import React, { useState, useCallback, useEffect } from 'react'
import { Box, Typography, LinearProgress, Icon, IconButton } from '@mui/material'
import AdxDropzone from 'components/reusable/AdxDropzone'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { _calculateFileSize } from 'services/helper/FileSize'
import AdxButton from 'components/custom/AdxButton'

const PayrollAttachmentUpload = ({ setFieldValue, resetForm, errorMessage = '', values }) => {
  const [uploadState, setUploadState] = useState('PENDING')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [files, setFiles] = useState([])
  const [errFiles, setErrFiles] = useState([])

  useEffect(() => {
    if (values.file && values.file.length !== 0) {
      setUploadState('READY')
      setFiles(values.file)
    }
  }, [values.file])

  const handleUpload = useCallback(async (acceptedFiles, fileRejections) => {
    // This function will be handled later with API call using axios.
    setUploadState('UPLOADING')
    if (fileRejections.length && !acceptedFiles.length) {
      setUploadState('NOT_READY')
      setFiles([])
      setErrFiles(fileRejections.map((file) => file))
      return
    }
    let csvData = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    )
    setFieldValue('file', csvData)
    setFiles(csvData)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelUpload = () => {
    setUploadState('PENDING')
    setUploadProgress(0)
    setFiles([])
    setErrFiles([])
    resetForm()
  }
  return (
    <Box display="flex" flexDirection="column" sx={{ gap: 1 }}>
      {uploadState === 'PENDING' ? (
        <AdxDropzone
          mainText="Upload CSV file"
          optionalText={`Upload file from your device or drag & drop here`}
          setUploadState={setUploadState}
          useDropzoneObject={{
            accept: [
              '.csv',
              'application/vnd.ms-excel',
              'text/csv',
              '.xlsx',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ],
            maxSize: 2097152,
            maxFiles: 1,
            onDrop: handleUpload,
          }}
        />
      ) : (
        <Box
          sx={{
            minWidth: '250px',
            minHeight: '350px',
            height: '100%',
            width: '100%',
            borderRadius: '4.5px',
            border: 1,
            borderColor: 'gray-line.main',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {uploadState === 'READY' ? (
            <>
              {files[0]?.type?.includes('image') ? (
                <Box as="img" src={files[0]?.preview} width="100%" height="100%" borderRadius={4} />
              ) : (
                <Box
                  sx={{
                    backgroundColor: 'gray-background.main',
                    flex: 1,
                    width: '100%',
                    borderRadius: '4.5px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: '5rem',
                      color: 'light-gray-text-2.main',
                    }}
                  >
                    file_copy
                  </Icon>
                </Box>
              )}
            </>
          ) : (
            <Box
              sx={{
                backgroundColor: 'gray-background.main',
                flex: 1,
                width: '100%',
                borderRadius: '4.5px',
              }}
            />
          )}

          {uploadState === 'UPLOADING' && (
            <LinearProgress
              variant="determinate"
              value={uploadProgress}
              color="alternate"
              sx={{
                width: '100%',
              }}
            />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              p: 2,
              gap: 1,
            }}
          >
            {uploadState === 'NOT_READY'
              ? errFiles.map(({ file, errors }, index) => (
                  <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, alignItems: 'center' }}>
                    <Box>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="start"
                        columnGap={1}
                        key={index}
                        sx={{ gap: 1 }}
                      >
                        <Icon sx={{ color: 'error.main' }}>cancel</Icon>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {file.name}
                          </Typography>
                          <Typography variant="body2" color="error.main" key={index}>
                            {errors[0].message}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Box>
                      <IconButton onClick={handleCancelUpload}>
                        <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                      </IconButton>
                    </Box>
                  </Box>
                ))
              : files.map((file, index) => (
                  <Box
                    display="flex"
                    flexDirection="column"
                    rowGap={1}
                    key={index}
                    sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' } }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      {file.name}
                    </Typography>
                    <Typography variant="body2">{_calculateFileSize(file.size)}</Typography>
                  </Box>
                ))}

            {uploadState === 'UPLOADING' && (
              <Box display="flex" alignItems="center" columnGap={1}>
                <Typography
                  variant="body1"
                  color="light-gray-text.main"
                >{`Uploading (${uploadProgress.toFixed(0)}%)`}</Typography>
                <IconButton onClick={handleCancelUpload}>
                  <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                </IconButton>
              </Box>
            )}

            {uploadState === 'READY' && (
              <Box display="flex" alignItems="center" columnGap={1}>
                <AdxButton variant={'ghost'} size="small" onClick={handleCancelUpload}>
                  Replace
                </AdxButton>
                <AdxButton variant={'ghost'} size="small">
                  View
                </AdxButton>
                <IconButton onClick={handleCancelUpload}>
                  <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                </IconButton>
              </Box>
            )}
          </Box>
        </Box>
      )}
      {errorMessage && <AdxErrorMessage>{errorMessage}</AdxErrorMessage>}
    </Box>
  )
}

export default PayrollAttachmentUpload
